import { useEffect, useState } from "react";
import { portalApi } from "../api";
import { errorText } from "../errors";
import { useI18n } from "../i18n";
import type { MyTitle } from "../types";
import { EmptyState, Link, Loading, Notice, PageTitle, Poster } from "../ui";
import { InformationRail } from "../information";

/**
 * A play account's whole portal (ORG-6, Sigge 2026-10-06: "you will only see
 * a carousel with My content"): one carousel of what the organisation has
 * made available - its picks, or the full library - plus any rental still
 * running. A film opens the player; a series its page, to choose an episode.
 */
export function ViewerHome() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState<MyTitle[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    portalApi
      .myTitles(lang)
      .then(
        (r) =>
          live &&
          setItems(
            r.items.filter(
              (x) =>
                x.rental?.state === "active" ||
                x.rental?.state === "not_started"
            )
          )
      )
      .catch((err) => live && setError(errorText(err, t)));
    return () => {
      live = false;
    };
  }, [lang, t]);

  if (error) return <Notice kind="error">{error}</Notice>;
  if (!items) return <Loading />;

  return (
    <div className="viewer-home">
      <PageTitle>{t("nav.my")}</PageTitle>
      {items.length === 0 ? (
        <EmptyState title={t("viewer.emptyTitle")}>
          {t("viewer.empty")}
        </EmptyState>
      ) : (
        <section className="rail" aria-label={t("nav.my")}>
          <ul className="rail__track">
            {items.map((x) => {
              const series = x.type === "series";
              const to = series
                ? `/titles/${encodeURIComponent(x.assetId)}`
                : `/watch/${encodeURIComponent(x.assetId)}`;
              return (
                <li key={x.assetId} className="title-card">
                  <Link
                    to={to}
                    className="card"
                    aria-label={
                      series ? x.title : t("viewer.play", { title: x.title })
                    }
                  >
                    <span className="card__media">
                      <Poster
                        src={x.images.landscape ?? x.images.poster}
                        title={x.title}
                        shape="landscape"
                      />
                    </span>
                    <span className="title-card__name">{x.title}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      <InformationRail />
    </div>
  );
}
